import { onMounted, onUnmounted } from "vue";
import { useBackgroundStore, type BackgroundRegionId } from "@/stores/background";

interface SetTemporaryBackgroundEvent {
  id: BackgroundRegionId;
  image: string;
  imageUrl?: string;
}

interface ClearTemporaryBackgroundEvent {
  id: BackgroundRegionId;
}

function getWujieBus() {
  return (window as any).$wujie?.bus;
}

export function emitToHost(eventName: string, data?: any) {
  const bus = getWujieBus();
  if (bus?.$emit) {
    bus.$emit(eventName, data);
  } else if (window.parent !== window) {
    window.parent.postMessage(
      { type: "wujie-event-from-child", eventName, data },
      "*"
    );
  }
}

export function emitBackgroundClick(id: BackgroundRegionId) {
  emitToHost("background-click", { id });
  emitToHost("background-image-click", { id });
}

export function useMicroFrontend() {
  const backgroundStore = useBackgroundStore();

  function handleSetTemporaryBackground(data: SetTemporaryBackgroundEvent) {
    const imageUrl = data?.image ?? data?.imageUrl;
    if (data?.id && imageUrl) {
      backgroundStore.setTemporaryBackground(data.id, imageUrl);
    }
  }

  function handleClearTemporaryBackground(data: ClearTemporaryBackgroundEvent) {
    if (data?.id) {
      backgroundStore.clearTemporaryBackground(data.id);
    }
  }

  function handleRefreshBackgrounds() {
    backgroundStore.refreshBackgrounds();
  }

  function handlePostMessage(event: MessageEvent) {
    const { type, eventName, data } = event.data || {};
    if (type === "wujie-event" || type === "wujie-event-from-child" || eventName) {
      const actualEventName = eventName || (event.data as any)?.eventName;
      const actualData = data || (event.data as any)?.data;

      switch (actualEventName) {
        case "set-temporary-background":
          handleSetTemporaryBackground(actualData);
          break;
        case "clear-temporary-background":
          handleClearTemporaryBackground(actualData);
          break;
        case "refresh-backgrounds":
          handleRefreshBackgrounds();
          break;
      }
    }
  }

  // 从 URL 参数读取并设置预览图片
  function applyPreviewImagesFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);

    // 支持格式1: ?preview=hero-banner:https://xx.png,kline-background:https://yy.png
    const previewParam = urlParams.get("preview");
    if (previewParam && previewParam !== "true") {
      const pairs = previewParam.split(",");
      for (const pair of pairs) {
        const [id, url] = pair.split(":");
        if (id && url) {
          backgroundStore.setTemporaryBackground(id as any, url);
        }
      }
    }

    // 支持格式2: ?preview=true&backgroundId=hero-banner&tempImageUrl=https://xx.png
    const checkIsPreview = urlParams.get("preview") === "true";
    if (checkIsPreview) {
      const backgroundId = urlParams.get("backgroundId");
      const tempImageUrl = urlParams.get("tempImageUrl");
      if (backgroundId && tempImageUrl) {
        backgroundStore.setTemporaryBackground(
          backgroundId as BackgroundRegionId,
          tempImageUrl
        );
      }
    }

    // 支持格式3: ?hero-banner=https://xx.png&kline-background=https://yy.png
    const regionIds: BackgroundRegionId[] = [
      "hero-banner",
      "kline-background",
      "announcement-image",
      "interaction-image",
      "education-image",
    ];
    for (const id of regionIds) {
      const url = urlParams.get(id);
      if (url) {
        backgroundStore.setTemporaryBackground(id, url);
      }
    }
  }

  // 检查是否为预览模式
  function isPreviewMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("preview") === "true";
  }

  onMounted(() => {
    const bus = getWujieBus();
    if (bus?.$on) {
      bus.$on("set-temporary-background", handleSetTemporaryBackground);
      bus.$on("clear-temporary-background", handleClearTemporaryBackground);
      bus.$on("refresh-backgrounds", handleRefreshBackgrounds);
    }
    window.addEventListener("message", handlePostMessage);

    const shouldEnableEdit = getEnableBackgroundEditFromProps();
    if (shouldEnableEdit) {
      backgroundStore.setEditMode(true);
    }

    // 从 URL 参数加载预览图片
    applyPreviewImagesFromUrl();
  });

  onUnmounted(() => {
    const bus = getWujieBus();
    if (bus?.$off) {
      bus.$off("set-temporary-background", handleSetTemporaryBackground);
      bus.$off("clear-temporary-background", handleClearTemporaryBackground);
      bus.$off("refresh-backgrounds", handleRefreshBackgrounds);
    }
    window.removeEventListener("message", handlePostMessage);
  });

  return { emitBackgroundClick };
}

export function getEnableBackgroundEditFromProps(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("enableBackgroundEdit") === "true") {
    return true;
  }
  const wujieProps = (window as any).$wujie?.props;
  return !!wujieProps?.enableBackgroundEdit;
}

export function isPreviewMode(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("preview") === "true";
}
